import { buildPageHeader, requestGoChat } from "./sheet-ui.js";
/**
 * voice-v8 — Memory-Browse-View.
 *
 * Tag 10: Suche + Type-Filter.
 * Tag 18: Multi-Tag-Filter via klickbare Tags + aktive Filter-Pills.
 * Alle DOM-Konstruktion über createElement/textContent — keine User-Inputs
 * gehen jemals durch innerHTML.
 */

import {
  recentMemory,
  searchMemory,
  type MemoryHit,
  type MemoryType,
} from "./memory-api.js";

const TYPE_LABELS: Record<MemoryType | "any", string> = {
  any: "Alle",
  chat: "Gespräche",
  fact: "Fakten",
  note: "Notizen",
  project: "Projekte",
  project_chapter: "Buch-Kapitel",
  contact: "Kontakte",
  preference: "Einstellungen",
  url: "Links",
  task: "Aufgaben",
};

const TYPE_OPTIONS: Array<MemoryType | "any"> = [
  "any",
  "chat",
  "fact",
  "project_chapter",
  "project",
  "contact",
  "task",
  "note",
  "preference",
];

/**
 * Schlagworte, die auf JEDER Karte stehen und deshalb nichts unterscheiden.
 *
 * 2026-08-26 (Norm B9): Unter allen 26 Karten hingen dieselben zwei Pillen —
 * "auto" (auf jeder Zeile identisch) und "session:039cf6cd" (eine Kennung, die
 * niemand liest). Sie waren so laut wie der Inhalt darueber. Ein Schlagwort,
 * das ueberall steht, sagt nichts; es gehoert nicht auf die Karte.
 */
const HIDDEN_TAGS = new Set(["v7-import", "auto"]);
const HIDDEN_TAG_PREFIXES = ["session:"];

export class MemoryView {
  private input!: HTMLInputElement;
  private select!: HTMLSelectElement;
  private list!: HTMLUListElement;
  private status!: HTMLElement;
  private filterBar!: HTMLElement;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private loaded = false;
  private activeTags = new Set<string>();

  constructor(root: HTMLElement) {
    root.classList.add("memory-view");
    while (root.firstChild) root.removeChild(root.firstChild);

    // NORM 2026-07-21: der Pfeil fuehrt ZURUECK ZUM CHAT (wie auf allen
    // Vollseiten), nicht mehr nur ins Menue.
    root.appendChild(
      buildPageHeader("Erinnerungen", () => requestGoChat(), {
        reopenMenu: false,
      }),
    );

    const controls = document.createElement("div");
    controls.className = "memory-controls";

    this.input = document.createElement("input");
    this.input.type = "search";
    // 2026-08-20 (beim Pruefen der Seite gefunden): "Erinnerungen
    // durchsuchen…" brauchte mehr als die 200px, die dem Feld neben dem
    // Filter bleiben — der Platzhalter war abgeschnitten ("durchsuch…").
    // Auf der Seite ERINNERUNGEN steht das Wort ohnehin schon im Titel; die
    // kurze Fassung sagt dasselbe und passt.
    this.input.placeholder = "Durchsuchen…";
    this.input.autocomplete = "off";
    this.input.spellcheck = false;
    controls.appendChild(this.input);

    this.select = document.createElement("select");
    this.select.setAttribute("aria-label", "Typ-Filter");
    for (const t of TYPE_OPTIONS) {
      const opt = document.createElement("option");
      opt.value = t;
      opt.textContent = TYPE_LABELS[t];
      this.select.appendChild(opt);
    }
    controls.appendChild(this.select);
    root.appendChild(controls);

    this.filterBar = document.createElement("div");
    this.filterBar.className = "memory-filter-bar";
    this.filterBar.hidden = true;
    root.appendChild(this.filterBar);

    this.status = document.createElement("div");
    this.status.className = "memory-status";
    root.appendChild(this.status);

    this.list = document.createElement("ul");
    this.list.className = "memory-list";
    root.appendChild(this.list);

    this.input.addEventListener("input", () => this.scheduleQuery());
    this.select.addEventListener("change", () => this.runQuery(true));
  }

  async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    await this.runQuery(false);
  }

  toggleTag(tag: string): void {
    if (this.activeTags.has(tag)) {
      this.activeTags.delete(tag);
    } else {
      this.activeTags.add(tag);
    }
    this.renderFilterBar();
    this.runQuery(true);
  }

  private renderFilterBar(): void {
    while (this.filterBar.firstChild)
      this.filterBar.removeChild(this.filterBar.firstChild);
    if (this.activeTags.size === 0) {
      this.filterBar.hidden = true;
      return;
    }
    this.filterBar.hidden = false;
    const label = document.createElement("span");
    label.className = "memory-filter-label";
    label.textContent = "Filter:";
    this.filterBar.appendChild(label);

    for (const tag of this.activeTags) {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "memory-filter-pill";
      pill.setAttribute("aria-label", `Tag ${tag} entfernen`);
      const txt = document.createElement("span");
      txt.textContent = tag;
      const x = document.createElement("span");
      x.className = "memory-filter-x";
      x.textContent = "×";
      pill.appendChild(txt);
      pill.appendChild(x);
      pill.addEventListener("click", () => this.toggleTag(tag));
      this.filterBar.appendChild(pill);
    }

    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "memory-filter-clear";
    clear.textContent = "alle entfernen";
    clear.addEventListener("click", () => {
      this.activeTags.clear();
      this.renderFilterBar();
      this.runQuery(true);
    });
    this.filterBar.appendChild(clear);
  }

  private scheduleQuery(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.runQuery(false), 300);
  }

  private async runQuery(immediate: boolean): Promise<void> {
    if (this.debounceTimer && !immediate) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    const q = this.input.value.trim();
    const type = this.select.value as MemoryType | "any";
    const tags = Array.from(this.activeTags);
    this.status.textContent = "lädt…";
    try {
      const items = q
        ? await searchMemory(q, { type, limit: 20, tags })
        : await recentMemory({ type, limit: 30, tags });
      this.render(items, q);
    } catch (err) {
      this.status.textContent =
        err instanceof Error ? `Fehler: ${err.message}` : "Fehler";
      while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
    }
  }

  private render(items: MemoryHit[], query: string): void {
    while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
    if (items.length === 0) {
      this.status.textContent = query
        ? `keine Treffer für "${query}"`
        : this.activeTags.size > 0
          ? "keine Treffer mit diesen Tags"
          : "Memory ist leer";
      return;
    }
    const filterDesc = query ? `Treffer für "${query}"` : "neueste Einträge";
    const tagDesc =
      this.activeTags.size > 0
        ? ` · ${this.activeTags.size} Tag-Filter aktiv`
        : "";
    this.status.textContent = `${items.length} ${filterDesc}${tagDesc}`;

    // 2026-08-26 (Norm B6): Jede Karte begann mit demselben Vorspann —
    // "Fakten 26.08.2026, 13:43" stand auf 26 Zeilen hintereinander gleich.
    // Etikett und Datum erscheinen jetzt nur, wenn sie etwas Neues sagen.
    let vorigerTyp = "";
    let vorigesDatum = "";
    for (const item of items) {
      const datum = formatDate(item.createdAt);
      this.list.appendChild(
        buildItem(item, (tag) => this.toggleTag(tag), this.activeTags, {
          zeigeTyp: item.type !== vorigerTyp,
          zeigeDatum: datum !== vorigesDatum,
        }),
      );
      vorigerTyp = item.type;
      vorigesDatum = datum;
    }
  }
}

function buildItem(
  item: MemoryHit,
  onTagClick: (tag: string) => void,
  activeTags: Set<string>,
  /** Was diese Karte gegenueber der vorigen NEUES sagt (Norm B6). */
  neu: { zeigeTyp: boolean; zeigeDatum: boolean } = { zeigeTyp: true, zeigeDatum: true },
): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "memory-item";

  const meta = document.createElement("div");
  meta.className = "memory-meta";

  if (neu.zeigeTyp) {
    const typeSpan = document.createElement("span");
    typeSpan.className = "memory-type";
    typeSpan.textContent = TYPE_LABELS[item.type] ?? item.type;
    meta.appendChild(typeSpan);
  }

  if (neu.zeigeDatum) {
    const dateSpan = document.createElement("span");
    dateSpan.className = "memory-date";
    dateSpan.textContent = formatDate(item.createdAt);
    meta.appendChild(dateSpan);
  }

  if (typeof item.score === "number") {
    const scoreSpan = document.createElement("span");
    scoreSpan.className = "memory-score";
    scoreSpan.textContent = item.score.toFixed(2);
    meta.appendChild(scoreSpan);
  }

  // ⚠️ Erst HIER pruefen, nicht vorher: der Bewertungswert kommt nach den
  // beiden Bedingungen dazu. Ein `remove()` davor waere wirkungslos gewesen,
  // weil `meta` zu dem Zeitpunkt noch gar nicht im Baum haengt.
  // Sagt die Kopfzeile nichts Neues, wird sie gar nicht erst angehaengt —
  // sonst bliebe eine leere Zeile stehen, die nur Platz kostet.
  if (meta.childElementCount) li.appendChild(meta);

  const content = document.createElement("div");
  content.className = "memory-content";
  content.textContent = item.content.slice(0, 400);
  li.appendChild(content);

  const tagBox = document.createElement("div");
  tagBox.className = "memory-tags";
  const sichtbareTags = item.tags.filter(
    (t) => !HIDDEN_TAGS.has(t) && !HIDDEN_TAG_PREFIXES.some((p) => t.startsWith(p)),
  );
  for (const t of sichtbareTags.slice(0, 6)) {
    const pill = document.createElement("button");
    pill.type = "button";
    pill.className = "memory-tag";
    if (activeTags.has(t)) pill.classList.add("active");
    pill.textContent = t;
    pill.title = activeTags.has(t)
      ? `Tag-Filter "${t}" entfernen`
      : `Nach Tag "${t}" filtern`;
    pill.addEventListener("click", () => onTagClick(t));
    tagBox.appendChild(pill);
  }
  li.appendChild(tagBox);

  return li;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
